/**
 * Landing Page — A/B tested with 3 sticky variants.
 *
 * Variant assigned on first visit via localStorage, tracked via GA4.
 * - reveal: Split hero + floating report card + why cards
 * - contrast: Side-by-side "them vs us" comparison panels
 * - editorial: Serif italic headline + browser-window showcase + how it works
 */

import { useEffect, lazy, Suspense } from 'react'
import TopNav from '@/components/TopNav'
import { useRouteSEO } from '@/hooks/useSEO'
import { getLandingVariant, trackLandingView } from '@/utils/abVariant'

// All below-the-fold + variant content is lazy. SharedSections imports
// motion/react (heavy: motion-dom + framer-motion drag controls), so
// keeping it eager pulls ~25KB of unused-on-paint code into the main
// bundle. Lazy → motion lives in chunks loaded after first paint.
const VariantReveal = lazy(() => import('@/components/landing/VariantReveal'))
const VariantContrast = lazy(() => import('@/components/landing/VariantContrast'))
const VariantEditorial = lazy(() => import('@/components/landing/VariantEditorial'))
const Footer = lazy(() => import('@/components/Footer'))
const TechBar = lazy(() =>
  import('@/components/landing/SharedSections').then(m => ({ default: m.TechBar }))
)
const CTASection = lazy(() =>
  import('@/components/landing/SharedSections').then(m => ({ default: m.CTASection }))
)
const FounderContact = lazy(() =>
  import('@/components/landing/SharedSections').then(m => ({ default: m.FounderContact }))
)

export default function LandingPageContrast() {
  useRouteSEO('/')
  const variant = getLandingVariant()

  useEffect(() => {
    trackLandingView(variant)
  }, [variant])

  return (
    <div className="min-h-screen bg-[#FAFBFC]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <TopNav />

      <Suspense fallback={<div className="min-h-[60vh]" />}>
        {variant === 'reveal' && <VariantReveal />}
        {variant === 'contrast' && <VariantContrast />}
        {variant === 'editorial' && <VariantEditorial />}
      </Suspense>

      <FounderContact />
      <TechBar />
      <CTASection variant={variant} />
      <Footer />
    </div>
  )
}
